import type { CockpitTab } from "../hooks/useCockpitTab";

interface TabDef {
  id: CockpitTab;
  label: string;
  icon: string;
}

// AUFTRAG M B5: 3 Tabs. Cockpit = reine Lage/Zahlen, Büro = Operatives
// (Mails, Anrufe, Termine, Pipeline), Team = Lohn/Mitarbeiter.
const TAB_DEFS: TabDef[] = [
  { id: "geld", label: "Cockpit", icon: "€" },
  { id: "buero", label: "Büro", icon: "🗂" },
  { id: "team", label: "Team", icon: "👤" },
];

interface Props {
  active: CockpitTab;
  onChange: (tab: CockpitTab) => void;
  badges?: Partial<Record<CockpitTab, number>>;
}

export default function CockpitTabBar({ active, onChange, badges }: Props) {
  return (
    <>
      <DesktopTabs active={active} onChange={onChange} badges={badges} />
      <MobileBottomTabs active={active} onChange={onChange} badges={badges} />
    </>
  );
}

function DesktopTabs({ active, onChange, badges }: Props) {
  return (
    <nav
      aria-label="Cockpit-Tabs"
      className="hidden md:flex gap-1 px-4 sm:px-6 py-2 border-t border-slate-800/40 bg-slate-950/60"
    >
      {TAB_DEFS.map((t) => {
        const isActive = active === t.id;
        const count = badges?.[t.id];
        return (
          <button
            key={t.id}
            role="tab"
            aria-selected={isActive}
            onClick={() => onChange(t.id)}
            className={`
              relative px-4 py-2 text-xs font-medium rounded-lg transition-colors
              ${isActive
                ? "bg-slate-800 text-slate-100"
                : "text-slate-400 hover:text-slate-200 hover:bg-slate-800/40"
              }
            `}
          >
            {t.label}
            {count != null && count > 0 && (
              <span className="ml-1.5 inline-flex items-center justify-center h-4 min-w-[16px] px-1 text-[10px] font-bold rounded-full bg-amber-500/20 text-amber-200 tabular-nums">
                {count > 99 ? "99+" : count}
              </span>
            )}
            {isActive && (
              <span className="absolute bottom-0 left-2 right-2 h-0.5 rounded-full bg-sky-400" />
            )}
          </button>
        );
      })}
    </nav>
  );
}

function MobileBottomTabs({ active, onChange, badges }: Props) {
  return (
    <nav
      aria-label="Cockpit-Tabs"
      className="md:hidden fixed bottom-0 inset-x-0 z-20 border-t border-slate-800/60 bg-slate-950/95 backdrop-blur-md pb-[env(safe-area-inset-bottom)]"
    >
      <div className="flex">
        {TAB_DEFS.map((t) => {
          const isActive = active === t.id;
          const count = badges?.[t.id];
          return (
            <button
              key={t.id}
              role="tab"
              aria-selected={isActive}
              onClick={() => onChange(t.id)}
              className={`
                flex-1 flex flex-col items-center gap-0.5 py-2 min-h-[44px] transition-colors
                ${isActive ? "text-sky-400" : "text-slate-500"}
              `}
            >
              <span className="relative text-base leading-none">
                {t.icon}
                {count != null && count > 0 && (
                  <span className="absolute -top-1 -right-2.5 h-3.5 min-w-[14px] px-0.5 text-[9px] font-bold rounded-full bg-amber-500 text-slate-950 flex items-center justify-center tabular-nums">
                    {count > 9 ? "9+" : count}
                  </span>
                )}
              </span>
              <span className="text-[10px] font-medium">{t.label}</span>
              {isActive && (
                <span className="absolute top-0 left-3 right-3 h-0.5 rounded-full bg-sky-400" />
              )}
            </button>
          );
        })}
      </div>
    </nav>
  );
}
